import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, MapPin, Search } from "lucide-react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";

import { db } from "../web/src/firebaseClient";
import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const q = query(
          collection(db, "events"),
          orderBy("eventDate", "asc")
        );

        const snapshot = await getDocs(q);

        const now = new Date();

        const data = snapshot.docs
          .map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
          }))
          .filter((eventItem) => {
            if (!eventItem.isActive) return false;

            const startDate = eventItem.startDate?.toDate?.();
            const endDate = eventItem.endDate?.toDate?.();

            if (startDate && now < startDate) return false;
            if (endDate && now > endDate) return false;

            return true;
          });

        setEvents(data);
      } catch (error) {
        console.error("Etkinlikler alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    if (!keyword) return events;

    return events.filter((eventItem) => {
      const title = eventItem.title?.toLowerCase() || "";
      const location = eventItem.location?.toLowerCase() || "";

      return (
        title.includes(keyword) ||
        location.includes(keyword)
      );
    });
  }, [events, searchText]);

  return (
    <main
      className="grid min-h-screen grid-rows-[auto_1fr_auto] bg-[#eef5ff]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6 md:px-6">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Etkinlikler
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Yaklaşan etkinlikleri buradan takip edebilirsiniz.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3 rounded-xl bg-[#f8fbff] px-4 py-3">
              <Search size={18} className="text-gray-400" />

              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Etkinlik veya yer ara..."
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl bg-white p-6 text-sm text-gray-500 shadow-sm">
              Etkinlikler yükleniyor...
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
              Yayında etkinlik bulunmuyor.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {filteredEvents.map((eventItem) => (
                <EventCard
                  key={eventItem.id}
                  eventItem={eventItem}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function EventCard({ eventItem }) {
  const eventDate = formatDate(eventItem.eventDate);

  return (
    <article className="group overflow-hidden rounded-2xl bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="aspect-video w-full overflow-hidden bg-gray-100">
        {eventItem.imageUrl ? (
          <img
            src={eventItem.imageUrl}
            alt={eventItem.title || "Etkinlik görseli"}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Görsel yok
          </div>
        )}
      </div>

      <div className="space-y-3 p-5">
        <h2 className="line-clamp-2 text-lg font-semibold text-[#173b7a]">
          {eventItem.title || "Başlıksız Etkinlik"}
        </h2>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CalendarDays
            size={17}
            className="text-[#1565C0]"
          />
          <span>{eventDate}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <MapPin
            size={17}
            className="text-[#C62828]"
          />
          <span>
            {eventItem.location || "Yer belirtilmedi"}
          </span>
        </div>
      </div>
    </article>
  );
}

function formatDate(dateValue) {
  if (!dateValue) return "Tarih belirtilmedi";

  const date = dateValue.toDate
    ? dateValue.toDate()
    : new Date(dateValue);

  return date.toLocaleString("tr-TR", {
    dateStyle: "long",
    timeStyle: "short",
  });
}